import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRbac } from '../../context/RbacContext';
import { can as canLocal, hasAnyRole } from '../../utils/permissions';

/**
 * Route guard. Renders children only when the logged-in user has any of the
 * supplied roles OR the named permission. Otherwise redirects to /no-access
 * (or to /login if the user isn't authenticated yet).
 *
 *   <RequireRole roles={['ADMIN','DOCTOR']}>
 *     <Patients />
 *   </RequireRole>
 *
 *   <RequireRole permission="page.billing">
 *     <Billing />
 *   </RequireRole>
 */
const RequireRole = ({ roles, permission, children }) => {
  const { user, loading: authLoading } = useAuth();
  const { can: canRbac, loading: rbacLoading } = useRbac();
  const location = useLocation();

  if (authLoading || rbacLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
        Checking access…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  let allowed = true;

  // Role-list check (legacy / override)
  if (roles && roles.length) {
    allowed = hasAnyRole(user.roles, roles);
  }

  // Permission check — prefer live RBAC manifest, fall back to local table
  if (allowed && permission) {
    if (canRbac(permission) === false && !canLocal(user.roles, permission)) {
      allowed = false;
    }
  }

  if (!allowed) {
    return <Navigate to="/no-access" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireRole;
